import { startTransition, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  useRideEmptyStationsAllowed,
  useRideStationActions,
} from "@ryde/redux";
import {
  DirectionEnum,
  DropdownAvailableIconsEnum,
  dropdownPassengersWithStationsAtom,
  dropdownStationListAtom,
  filterHomeStationsCategory,
  filterWorkStationsCategory,
  getDestinationStationsIdsFromPassengers,
  getRealRouteStationIds,
  getSourceStationsIdsFromPassengers,
  IDropdownItem,
  IPassengerForStationDto,
  IRideDto,
  isTranslatableItem,
  Log,
  rideSelectedPassengersIdsAF,
  RideStationPanelMode,
  sortDropdownItemsByArray,
  stationsForHomeSelector,
  stationsForWorkSelector,
  useDrawers,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { IRideBulkActionsFormModel } from "./ride-station-bulk-actions.models";

export const useRideStationBulkActions = (rideDto: Partial<IRideDto>) => {
  const { t } = useTranslation();
  const formMethods = useForm<IRideBulkActionsFormModel>({
    defaultValues: {
      stationBulkFromId: undefined,
      stationBulkToId: undefined,
      onlyExistingBulkStations: false,
    },
  });
  const {
    isRideWithEmptyStationsAllowed,
    isRideWithDuplicatedStationsAllowed,
  } = useRideEmptyStationsAllowed(RideStationPanelMode.CRUD_TAB, rideDto);

  const isSameOriginOrDestinationAllowed = useMemo(() => {
    return Boolean(
      isRideWithEmptyStationsAllowed || isRideWithDuplicatedStationsAllowed,
    );
  }, [isRideWithDuplicatedStationsAllowed, isRideWithEmptyStationsAllowed]);

  const [selectedPassengersIds, setSelectedPassengersIds] = useRecoilState(
    rideSelectedPassengersIdsAF(rideDto?.id || 0),
  );

  const isPickUp = rideDto?.direction === DirectionEnum.PickUp;

  const stationsForHomeCategories = useRecoilValue(stationsForHomeSelector);
  const stationsForWorkCategories = useRecoilValue(stationsForWorkSelector);
  const stationsDropdownList = useRecoilValue(dropdownStationListAtom);
  const passengersDropdownList = useRecoilValue(
    dropdownPassengersWithStationsAtom,
  );

  const selectedPassengers: IPassengerForStationDto[] = useMemo(() => {
    if (!passengersDropdownList?.length || !selectedPassengersIds?.length) {
      return [];
    }

    const selectedPassengersIdsSet = new Set(selectedPassengersIds);
    return passengersDropdownList.filter(x =>
      selectedPassengersIdsSet.has(x.id),
    );
  }, [passengersDropdownList, selectedPassengersIds]);

  const existingStationIds: number[] = useMemo(() => {
    return getRealRouteStationIds(rideDto?.metadata?.stationsInfo);
  }, [rideDto?.metadata?.stationsInfo]);

  const { getValues, setValue, watch } = formMethods;
  const {
    bulkUpdateRideStations,
    fetchStations,
    fetchDropdownPassengersAndStations,
  } = useRideStationActions();
  const { openEditFormDrawer } = useDrawers();

  const onlyExistingBulkStations = watch("onlyExistingBulkStations");
  const stationFromWatchedId: number | undefined = watch("stationBulkFromId");
  const stationToWatchedId: number | undefined = watch("stationBulkToId");

  useEffect(() => {
    startTransition(() => {
      fetchDropdownPassengersAndStations();
    });
  }, [fetchDropdownPassengersAndStations, setValue]);

  const deselectAllPassengers = (): void => {
    setSelectedPassengersIds([]);
  };

  const openStationFromCard = useEvent(() => {
    if (!stationFromWatchedId) {
      return;
    }

    openEditFormDrawer({
      entity: "station",
      id: stationFromWatchedId,
    });
  });

  const addStationFromHandler = useEvent(async (newId: number) => {
    if (!newId) {
      return;
    }

    try {
      await fetchStations(true);
      setValue("stationBulkFromId", newId);
    } catch (e) {
      Log.warnAndToast(t("errors.errorFetchingStations"), e);
    }
  });

  const addStationToHandler = useEvent(async (newId: number) => {
    if (!newId) {
      return;
    }

    try {
      await fetchStations(true);
      setValue("stationBulkToId", newId);
    } catch (e) {
      Log.warnAndToast(t("errors.errorFetchingStations"), e);
    }
  });

  const openStationToCard = useEvent(() => {
    if (!stationToWatchedId) {
      return;
    }

    openEditFormDrawer({
      entity: "station",
      id: stationToWatchedId,
    });
  });

  const bulkActionHandler = useEvent(async () => {
    const stationFromId = getValues("stationBulkFromId");
    const stationToId = getValues("stationBulkToId");

    if (!stationFromId && !stationToId) {
      Log.warnAndToast(t("errors.chooseStationsForBulkUpdate"));
      return;
    }

    const res = await bulkUpdateRideStations({
      rideDto,
      passengerIds: selectedPassengersIds,
      stationFromId,
      stationToId,
    });

    if (res.success) {
      Log.debug("Bulk action performed successfully");

      deselectAllPassengers();

      setValue("stationBulkFromId", undefined);
      setValue("stationBulkToId", undefined);
    } else {
      if (isTranslatableItem(res?.error)) {
        warnAndToastSimpleResult(res, t);
      } else if (res?.error) {
        Log.warnAndToast(t("errors.errorPreformingBulkAction"), res?.error);
      }
    }
  });

  const optionIconMapping = useMemo(() => {
    const optionIconMap = new Map();

    stationsDropdownList?.forEach(station => {
      if (station.id && !optionIconMap.has(station.id) && !station.isOfficial) {
        optionIconMap.set(station.id, DropdownAvailableIconsEnum.homeIcon);
      }
    });

    return optionIconMap;
  }, [stationsDropdownList]);

  const optionIconMappingFrom = useMemo(() => {
    if (isPickUp) {
      return optionIconMapping;
    }

    return undefined;
  }, [isPickUp, optionIconMapping]);

  const optionIconMappingTo = useMemo(() => {
    if (!isPickUp) {
      return optionIconMapping;
    }

    return undefined;
  }, [isPickUp, optionIconMapping]);

  const boldStationsInIds = useMemo(() => {
    return getSourceStationsIdsFromPassengers({
      passengers: selectedPassengers,
    });
  }, [selectedPassengers]);

  const boldStationsOutIds = useMemo(() => {
    return getDestinationStationsIdsFromPassengers({
      passengers: selectedPassengers,
    });
  }, [selectedPassengers]);

  const boldStationsFromIds = useMemo(
    () => (isPickUp ? boldStationsInIds : boldStationsOutIds),
    [boldStationsInIds, boldStationsOutIds, isPickUp],
  );

  const boldStationsToIds = useMemo(
    () => (isPickUp ? boldStationsOutIds : boldStationsInIds),
    [boldStationsInIds, boldStationsOutIds, isPickUp],
  );

  const stationsDropdownListFiltered: IDropdownItem[] = useMemo(() => {
    if (!onlyExistingBulkStations) {
      return stationsDropdownList || [];
    }

    const existingStationIdsSet = new Set(existingStationIds);
    const filteredList =
      stationsDropdownList?.filter(x => existingStationIdsSet.has(+x.value)) ||
      [];

    return filteredList;
  }, [existingStationIds, onlyExistingBulkStations, stationsDropdownList]);

  const stationsInDropdownList = useMemo(() => {
    if (!stationsDropdownListFiltered?.length) {
      if (stationFromWatchedId) {
        setValue("stationBulkFromId", undefined);
      }

      return [];
    }

    if (
      stationFromWatchedId &&
      !stationsDropdownListFiltered.some(
        x => Number(x.value) === Number(stationFromWatchedId),
      )
    ) {
      setValue("stationBulkFromId", undefined);
    }

    const dropdownListExcludedValueTo = isSameOriginOrDestinationAllowed
      ? stationsDropdownListFiltered
      : stationsDropdownListFiltered.filter(
          x => Number(x.value) !== Number(stationToWatchedId),
        );

    const sortedStationsList = sortDropdownItemsByArray(
      dropdownListExcludedValueTo,
      boldStationsFromIds,
    );

    if (isPickUp) {
      return filterHomeStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForHomeCategories,
        stationsDropdownList: sortedStationsList,
      });
    } else {
      return filterWorkStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForWorkCategories,
        stationsDropdownList: sortedStationsList,
      });
    }
  }, [
    boldStationsFromIds,
    isPickUp,
    isSameOriginOrDestinationAllowed,
    selectedPassengers,
    setValue,
    stationFromWatchedId,
    stationToWatchedId,
    stationsDropdownListFiltered,
    stationsForHomeCategories,
    stationsForWorkCategories,
  ]);

  const stationsOutDropdownList = useMemo(() => {
    if (!stationsDropdownListFiltered?.length) {
      if (stationToWatchedId) {
        setValue("stationBulkToId", undefined);
      }

      return [];
    }

    if (
      stationToWatchedId &&
      !stationsDropdownListFiltered.some(
        x => Number(x.value) === Number(stationToWatchedId),
      )
    ) {
      setValue("stationBulkToId", undefined);
    }

    const dropdownListExcludedValueFrom = isSameOriginOrDestinationAllowed
      ? stationsDropdownListFiltered
      : stationsDropdownListFiltered.filter(
          x => Number(x.value) !== Number(stationFromWatchedId),
        );

    const sortedStations = sortDropdownItemsByArray(
      dropdownListExcludedValueFrom,
      boldStationsToIds,
    );

    if (isPickUp) {
      return filterWorkStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForWorkCategories,
        stationsDropdownList: sortedStations,
      });
    } else {
      return filterHomeStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForHomeCategories,
        stationsDropdownList: sortedStations,
      });
    }
  }, [
    boldStationsToIds,
    isPickUp,
    isSameOriginOrDestinationAllowed,
    selectedPassengers,
    setValue,
    stationFromWatchedId,
    stationToWatchedId,
    stationsDropdownListFiltered,
    stationsForHomeCategories,
    stationsForWorkCategories,
  ]);

  return {
    stationsInDropdownList,
    stationsOutDropdownList,
    openStationFromCard,
    openStationToCard,
    addStationFromHandler,
    addStationToHandler,
    formMethods,
    stationFromWatchedId,
    stationToWatchedId,
    deselectAllPassengers,
    bulkActionHandler,
    selectedPassengersIds,
    optionIconMappingFrom,
    optionIconMappingTo,
    boldStationsFromIds,
    boldStationsToIds,
  };
};
